import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

const ALLOWED_EMAILS = ['angus@florance.com.au', 'karla@beatsandbounce.com'];

export function AuthProvider({ children }) {
  console.log('AuthProvider initializing...');

  const [user, setUser] = useState(() => {
    console.log('Loading user from localStorage...');
    const savedUser = localStorage.getItem('user');
    const parsedUser = savedUser ? JSON.parse(savedUser) : null;
    console.log('Loaded user:', parsedUser);
    return parsedUser;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);

  useEffect(() => {
    console.log('User state changed:', user);
    if (user) {
      console.log('Saving user to localStorage...');
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      console.log('Removing user from localStorage...');
      localStorage.removeItem('user');
    }
  }, [user]);

  const generateVerificationCode = () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    console.log('Generated verification code:', code);
    return code;
  };

  const sendVerificationEmail = async (email, code) => {
    console.log('Sending verification code to:', email);
    // In a real application, you would integrate with an email service like SendGrid
    // For demo purposes, we'll just console.log the code
    console.log(`Verification code for ${email}: ${code}`);
    alert(`For demo purposes, your verification code is: ${code}`);
  };

  const initiateLogin = async (email) => {
    console.log('Initiating login for:', email);
    setLoading(true);
    setError('');
    try {
      if (!ALLOWED_EMAILS.includes(email)) {
        console.error('Email not authorized:', email);
        throw new Error('Email not authorized');
      }

      const code = generateVerificationCode();
      setVerificationCode(code);
      setPendingEmail(email);
      await sendVerificationEmail(email, code);
      console.log('Login initiated successfully');
      
    } catch (err) {
      console.error('Login initiation error:', err);
      setError(err.message);
      setPendingEmail(null);
      setVerificationCode(null);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code) => {
    console.log('Verifying code:', code);
    setLoading(true);
    setError('');
    try {
      console.log('Stored verification code:', verificationCode);
      console.log('Pending email:', pendingEmail);
      if (code === verificationCode && pendingEmail) {
        const userData = { email: pendingEmail };
        console.log('Setting user data:', userData);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setVerificationCode(null);
        setPendingEmail(null);
        console.log('Verification successful');
        return true;
      } else {
        console.error('Invalid verification code or no pending email');
        throw new Error('Invalid verification code');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetLoginState = () => {
    console.log('Resetting login state');
    setPendingEmail(null);
    setVerificationCode(null);
    setError('');
  };

  const logout = () => {
    console.log('Logging out user');
    setUser(null);
    setPendingEmail(null);
    setVerificationCode(null);
    setError('');
    localStorage.removeItem('user');
  };

  const value = {
    user,
    loading,
    error,
    pendingEmail,
    initiateLogin,
    verifyCode,
    resetLoginState,
    logout
  };

  console.log('Current AuthContext state:', {
    user,
    loading,
    error,
    pendingEmail,
    hasVerificationCode: !!verificationCode
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    console.error('useAuth must be used within an AuthProvider');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 